import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {from, Observable, of} from 'rxjs';
import {Route, Routes} from '@angular/router';
import {catchError, map, switchMap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {RouteContext, ROUTE_CONTEXTS} from './route-context';
import {
  resolveComponent,
  resolveLazyModuleLoader,
  shouldTransformLoadChildren,
  usesSpreadRoute,
} from './route-registries';
import {CategoryLevelComponent} from '../category/shared/category-level/category-level.component';

const BROWSE_MOTORS_COMPONENT_DEPTH: Record<string, number> = {
  MotorsSubsubComponent: 2,
  MotorsSubsubsubComponent: 3,
  MotorsSubsubsubsubComponent: 4,
  MotorsSubsubsubsubsubComponent: 5,
  MotorsSubsubsubsubsubsubComponent: 6,
  MotorsSubsubsubsubsubsubsubComponent: 7,
  MotorsSubsubsubsubsubsubsubsubComponent: 8,
};

const CATEGORY_CONTEXT_DEPTH: Partial<Record<RouteContext, number>> = {
  subsub: 2,
  subsubsub: 3,
  subsubsubsub: 4,
  subsubsubsubsub: 5,
  subsubsubsubsubsub: 6,
  subsubsubsubsubsubsub: 7,
  subsubsubsubsubsubsubsub: 8,
  'add-item-subsub': 2,
  'add-item-subsubsub': 3,
  'add-item-subsubsubsub': 4,
  'add-item-subsubsubsubsub': 5,
  'add-item-subsubsubsubsubsub': 6,
  'add-item-subsubsubsubsubsubsub': 7,
  'add-item-subsubsubsubsubsubsubsub': 8,
};

const ADD_ITEM_COMPONENT_DEPTH: Record<string, number> = {
  AddItemSubsubComponent: 2,
  AddItemSubsubsubComponent: 3,
  AddItemSubsubsubsubComponent: 4,
  AddItemSubsubsubsubsubComponent: 5,
  AddItemSubsubsubsubsubsubComponent: 6,
  AddItemSubsubsubsubsubsubsubComponent: 7,
  AddItemSubsubsubsubsubsubsubsubComponent: 8,
};

@Injectable({
  providedIn: 'root',
})
export class DynamicRouteService {

  constructor(private http: HttpClient) {}

  private routingBaseUrl(): Observable<string> {
    return from(environment as Promise<Record<string, string>>).pipe(
      map(env => `${env['backendURL']}/routing`),
    );
  }

  loadRoutes(context: RouteContext, endpoint: string): Observable<Routes> {
    return this.routingBaseUrl().pipe(
      switchMap(baseUrl => this.http.get<any[]>(`${baseUrl}/${endpoint}`)),
      map(routes => this.transformRoutes(routes, context)),
    );
  }

  // Top-level category slugs (e.g. "test", "motors") used to tell a base
  // category apart from a profile id during URL parsing. Sourced from the
  // backend /categories list since the routing bootstrap only emits "" + ":id".
  loadBaseCategories(): Observable<string[]> {
    return from(environment as Promise<Record<string, string>>).pipe(
      switchMap(env => this.http.get<any[]>(`${env['backendURL']}/categories`)),
      map(categories => (categories || []).map(category => category?.url).filter(Boolean)),
      catchError(err => {
        console.error('Failed to load base categories; profile detection may be degraded', err);
        return of([] as string[]);
      }),
    );
  }

  loadBootstrap(): Observable<Partial<Record<RouteContext, any[]>>> {
    return this.routingBaseUrl().pipe(
      switchMap(baseUrl => this.http.get<Partial<Record<RouteContext, any[]>>>(`${baseUrl}/bootstrap`)),
      map(payload => {
        const transformed: Partial<Record<RouteContext, Routes>> = {};
        for (const context of ROUTE_CONTEXTS) {
          const routes = payload[context];
          if (routes) {
            // Isolate per-context transform failures so one malformed context
            // can't discard the entire (otherwise valid) bootstrap.
            try {
              transformed[context] = this.transformRoutes(routes, context);
            } catch (err) {
              console.error(`Failed to transform routes for context "${context}"; skipping`, err);
            }
          }
        }
        return transformed;
      }),
      // A bootstrap failure must not brick the SPA: fall back to an empty set so
      // the app still boots (the backend always emits base app routes when healthy).
      catchError(err => {
        console.error('Failed to load routing bootstrap; starting with no dynamic routes', err);
        return of({} as Partial<Record<RouteContext, any[]>>);
      }),
    );
  }

  loadTree(context: RouteContext): Observable<Routes> {
    return this.routingBaseUrl().pipe(
      switchMap(baseUrl => this.http.get<any[]>(`${baseUrl}/tree`, {params: {context}})),
      map(routes => this.transformRoutes(routes, context)),
    );
  }

  transformRoutes(routes: any[], context: RouteContext): Routes {
    const transformed: Routes = [];
    for (const route of routes) {
      try {
        transformed.push(this.transformRoute(route, context));
      } catch (err) {
        console.error(
          `Failed to transform route "${route?.path ?? ''}" in context "${context}"; skipping`,
          err,
        );
      }
    }
    return transformed;
  }

  private transformRoute(route: any, context: RouteContext): Route {
    const transformedRoute: any = usesSpreadRoute(context)
      ? {...route}
      : {path: route.path, data: route.data || {}};

    if (route.loadChildren && typeof route.loadChildren === 'string') {
      this.applyLoadChildren(transformedRoute, route, context);
    }

    if (route.component && typeof route.component === 'string') {
      this.applyComponent(transformedRoute, route.component, context);
    }

    if (route.redirectTo) {
      transformedRoute.redirectTo = route.redirectTo;
      if (!route.pathMatch) {
        transformedRoute.pathMatch = 'full';
      }
    }

    if (route.children) {
      transformedRoute.children = this.transformRoutes(route.children, context);
    }

    return transformedRoute;
  }

  private applyLoadChildren(transformedRoute: any, route: any, context: RouteContext): void {
    if (!shouldTransformLoadChildren(context)) {
      return;
    }

    const modulePath = route.loadChildren;
    const loader = resolveLazyModuleLoader(context, modulePath);
    if (loader) {
      transformedRoute.loadChildren = loader;
      return;
    }

    throw new Error(`Unknown module path: ${modulePath} (context: ${context})`);
  }

  private applyComponent(transformedRoute: any, componentName: string, context: RouteContext): void {
    const browseDepth = BROWSE_MOTORS_COMPONENT_DEPTH[componentName];
    if (browseDepth) {
      transformedRoute.component = CategoryLevelComponent;
      transformedRoute.data = {
        ...(transformedRoute.data || {}),
        categoryLevel: {depth: browseDepth, context: 'browse'},
      };
      return;
    }

    const addItemDepth = ADD_ITEM_COMPONENT_DEPTH[componentName];
    if (addItemDepth) {
      transformedRoute.component = CategoryLevelComponent;
      transformedRoute.data = {
        ...(transformedRoute.data || {}),
        categoryLevel: {depth: addItemDepth, context: 'add-item'},
      };
      return;
    }

    if (componentName === 'CategoryLevelComponent') {
      const depth = CATEGORY_CONTEXT_DEPTH[context];
      if (depth) {
        const categoryContext = context.startsWith('add-item') ? 'add-item' : 'browse';
        transformedRoute.component = CategoryLevelComponent;
        transformedRoute.data = {
          ...(transformedRoute.data || {}),
          categoryLevel: {depth, context: categoryContext},
        };
        return;
      }
    }

    if (context === 'sub' && componentName === 'ListingDetailComponent') {
      transformedRoute.component = resolveComponent('ListingDetailComponent');
      return;
    }

    const strict = context === 'app';
    const component = resolveComponent(componentName, strict);
    if (component) {
      transformedRoute.component = component;
    }
  }
}
